import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiClient, axiosClient, PermissionGate } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import TaskTimelogTab from '../components/TaskTimelogTab';
import TaskHistoryTab from '../components/TaskHistoryTab';
import TaskDependenciesCard from '../components/TaskDependenciesCard';
import TaskRecurrenceCard from '../components/TaskRecurrenceCard';
import TaskDocumentsTab from '../components/TaskDocumentsTab';
// Extracted out of this file so the employee task page renders the SAME
// checklist rather than a second copy — see the note on the component.
import TaskChecklistCard from '../components/TaskChecklistCard';
import TodoList from '../components/TodoList';
import InvoiceBuilder from '../components/finance/InvoiceBuilder';
import TaskCompletionFlow from '../components/TaskCompletionFlow';
import { Button, Card, Tabs, Modal, Select, Input, Skeleton, useToast } from '../../design-system';
import { formatServiceName } from '../../lib/serviceCatalog';
import { TASK_STATUSES, TASK_STATUS_LABEL, waitingAging, dueAging } from '../../lib/taskStatus';
import '../styles/OrgAdminTaskDashboard.css';

// The status list lives in lib/taskStatus so the six places that render it
// can't drift apart.
const STATUS_OPTIONS = TASK_STATUSES;
const STATUS_LABEL = TASK_STATUS_LABEL;

// Where each status sits on the Pending -> In-Progress -> Completed track.
// waiting-client and on-hold are both "started but stalled", so they sit on the
// middle step rather than getting one of their own.
const STATUS_STEP = {
  todo: 0, 'in-progress': 1, review: 1, 'waiting-client': 1, 'on-hold': 1, completed: 2,
};

// "Supporting Files" was a 🚧 placeholder duplicating what Documents now does
// properly — dropped rather than shipped empty.
const TABS = [
  { key: 'details', label: 'Details' },
  { key: 'timelog', label: 'Timelog' },
  { key: 'todo', label: 'To-Do' },
  { key: 'documents', label: 'Documents' },
  { key: 'history', label: 'Task History' },
];

const AVATAR_COLORS = ['#4f6f5c', '#9c5b3e', '#b08428', '#2b3a4a', '#6e6b66', '#7a6a9c'];

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join('');
}

function Avatar({ name, size = 30 }) {
  const color = AVATAR_COLORS[(name?.length || 0) % AVATAR_COLORS.length];
  return (
    <span
      className="td-avatar"
      style={{ width: size, height: size, background: color, fontSize: size * 0.42 }}
      title={name}
    >
      {initials(name)}
    </span>
  );
}

function fmtDate(value, withTime = false) {
  if (!value) return null;
  const d = new Date(value);
  return withTime
    ? d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// --- Icons (inline; the sidebar NavIcons set doesn't cover these) ---
const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M14.5 5.5l4 4M4 20l1-4L16 5a1.5 1.5 0 0 1 2 0l1 1a1.5 1.5 0 0 1 0 2L8 19Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const TrashSm = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4.5 7h15M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M7 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h5.4a1.5 1.5 0 0 0 1.5-1.4L17 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const CloneIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.6" /><path d="M5 15V6.5A1.5 1.5 0 0 1 6.5 5H15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const TimerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="13" r="7.5" stroke="currentColor" strokeWidth="1.7" /><path d="M12 9.5V13l2.5 1.5M9.5 2.5h5M12 2.5V5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const CloseX = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
);

function OrgAdminTaskDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  // The current user was only ever read to stamp "completed by" on a checklist
  // tick; TaskChecklistCard now reads it itself, so this page no longer needs it.
  const toast = useToast();

  const [task, setTask] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    axiosClient.get('/users').then((res) => setOrgUsers(res.data)).catch(() => {});
  }, []);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/api/tasks/${id}`);
      setTask(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  // Single partial-update helper for every inline edit. Optimistic: apply
  // `optimistic` immediately, PATCH, reconcile with the server copy, revert
  // on failure.
  const patchTask = async (payload, optimistic) => {
    const prev = task;
    if (optimistic) setTask({ ...task, ...optimistic });
    try {
      const res = await apiClient.patch(`/api/tasks/${id}`, payload);
      setTask(res.data);
      return res.data;
    } catch (err) {
      setTask(prev);
      toast.danger(err.response?.data?.error || 'Update failed');
      throw err;
    }
  };

  // Clone: don't create anything — carry this task's INITIAL-STAGE fields
  // (client, dates, assignees + budgeted hours, tags, priority…) to the Create
  // Task form as a prefill, so the user reviews/edits and submits themselves.
  // Live state (status, timelogs, checklist, completion) is deliberately left out.
  const cloneTask = () => {
    if (!task) return;
    const idOf = (v) => (v && typeof v === 'object' ? v._id : v) || '';
    const allocs = task.hourAllocations || [];
    const hoursForRole = (role) => {
      const a = allocs.find((x) => x.role === role);
      return a && a.allocatedHours != null ? String(a.allocatedHours) : '';
    };
    const assistantHours = {};
    allocs
      .filter((a) => a.role === 'assistant')
      .forEach((a) => { const uid = idOf(a.userId); if (uid) assistantHours[uid] = a.allocatedHours != null ? String(a.allocatedHours) : ''; });
    const clientIds = (task.clientId ? [task.clientId] : task.clientIds || []).map(idOf).filter(Boolean);

    const clone = {
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : '',
      tags: task.tags || [],
      clientIds,
      // The form resolves the display name from the catalog once it loads.
      selectedServiceId: idOf(task.serviceId),
      preparerId: idOf(task.preparerId),
      preparerHours: hoursForRole('preparer'),
      reviewerId: idOf(task.reviewerId),
      reviewerHours: hoursForRole('reviewer'),
      assistantIds: (task.assistantIds || []).map(idOf).filter(Boolean),
      assistantHours,
    };
    navigate('/org-admin/tasks/create', { state: { clone } });
  };

  const breadcrumb = (
    <span className="td-breadcrumb">
      <button className="td-back" onClick={() => navigate('/org-admin/tasks')} aria-label="Back to tasks">
        <BackIcon />
      </button>
      <Link to="/org-admin/tasks" className="td-crumb-link">Tasks</Link>
      <span className="td-crumb-sep">›</span>
      <span className="td-crumb-current">Task Dashboard</span>
    </span>
  );

  // Delete is a SOFT delete (server sets isDeleted=true; the row is kept and
  // recoverable). After deleting we go back to the list; viewing a task
  // that's already soft-deleted shows the restore banner below instead.
  const handleDelete = async () => {
    if (!window.confirm('Delete this task? It can be restored from Deleted Tasks.')) return;
    try {
      await apiClient.delete(`/api/tasks/${id}`);
      toast.success('Task deleted');
      navigate('/org-admin/tasks');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to delete task');
    }
  };

  const handleRestore = async () => {
    try {
      const res = await apiClient.patch(`/api/tasks/${id}/restore`);
      setTask(res.data);
      toast.success('Task restored');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to restore task');
    }
  };

  if (loading) {
    return (
      <OrgAdminLayout title={breadcrumb}>
        <Card><Skeleton.Text lines={6} /></Card>
      </OrgAdminLayout>
    );
  }
  if (error || !task) {
    return (
      <OrgAdminLayout title={breadcrumb}>
        <div className="td-error" role="alert">{error || 'Task not found'}</div>
      </OrgAdminLayout>
    );
  }

  const createdByName =
    orgUsers.find((u) => u._id === task.createdBy)?.name || task.createdByRole || 'Unknown';

  // Completed tasks are read-only everywhere except the reopen control.
  const locked = task.status === 'completed';

  return (
    <OrgAdminLayout
      title={breadcrumb}
      headerRight={
        <div className="td-header-actions">
          <PermissionGate requiredPermission="tasks.create">
            <Button variant="secondary" size="sm" onClick={cloneTask}>
              <CloneIcon /> Clone
            </Button>
          </PermissionGate>
          {!task.isDeleted && (
            <PermissionGate requiredPermission="tasks.delete">
              <Button variant="secondary" size="sm" onClick={handleDelete}>
                <TrashSm /> Delete
              </Button>
            </PermissionGate>
          )}
        </div>
      }
    >
      <div className="task-dash">
        {task.isDeleted && (
          <div className="td-deleted-banner">
            <span>
              🗑️ This task was deleted{task.deletedAt ? ` on ${fmtDate(task.deletedAt, true)}` : ''}. It&apos;s hidden
              from normal views but not gone — restore it to bring it back.
            </span>
            <PermissionGate requiredPermission="tasks.delete">
              <Button variant="primary" size="sm" onClick={handleRestore}>Restore Task</Button>
            </PermissionGate>
          </div>
        )}

        {task.status === 'completed' && !task.isDeleted && (
          <div className="td-locked-banner">
            <span>
              🔒 This task is <strong>completed</strong> and locked — nothing can be edited. Only its
              status can change, and only to <strong>reopen</strong> it. Use <strong>Reopen</strong>
              {' '}above to make changes.
            </span>
          </div>
        )}

        {/* Blocked on the client — the single most common real blocker, so it
            gets a banner with what's outstanding and for how long. */}
        {(() => {
          const waiting = waitingAging(task);
          if (!waiting) return null;
          return (
            <div className="td-waiting-banner">
              ⏳ Waiting on the client for <strong>{waiting.label}</strong>
              {task.waitingReason ? <> — {task.waitingReason}</> : null}
            </div>
          );
        })()}

        {/* Overdue is worth saying out loud on the task itself, not just in the
            list someone may not have come from. */}
        {(() => {
          const age = dueAging(task);
          if (age.tone !== 'overdue' || task.isDeleted) return null;
          return (
            <div className="td-overdue-banner">
              🔴 This task is <strong>{age.label}</strong> (due {fmtDate(task.dueDate)}).
            </div>
          );
        })()}

        <TaskHeaderRow
          task={task}
          patchTask={patchTask}
          onTimer={() => setActiveTab('timelog')}
          onComplete={() => setCompleting(true)}
        />

        <MetaSection task={task} patchTask={patchTask} locked={locked} />

        <div className="td-tabs">
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} layoutId="task-dash-tabs" />
        </div>

        {activeTab === 'details' && (
          <DetailsTab
            task={task}
            orgUsers={orgUsers}
            createdByName={createdByName}
            patchTask={patchTask}
            setTask={setTask}
            toast={toast}
            taskId={id}
            locked={locked}
          />
        )}
        {activeTab === 'timelog' && (
          <div className={locked ? 'td-locked-region' : ''}>
            <TaskTimelogTab task={task} setTask={setTask} orgUsers={orgUsers} />
          </div>
        )}
        {activeTab === 'history' && <TaskHistoryTab task={task} setTask={setTask} />}
        {activeTab === 'todo' && (
          <Card padding="md">
            <div className="td-card-head"><span className="td-card-title">To-Do</span></div>
            <div className={locked ? 'td-locked-region' : ''}>
              <TodoList taskId={id} enableDetailModal />
            </div>
          </Card>
        )}
        {/* Documents are real rows in the firm's document store, linked to this
            task — the same pipeline (S3 + OneDrive mirror, access logging,
            soft delete) as everywhere else. The old inline attachments array is
            still rendered underneath for tasks created before that existed. */}
        {activeTab === 'documents' && (
          <>
            <TaskDocumentsTab task={task} locked={locked} />
            <LegacyAttachments task={task} />
          </>
        )}
      </div>

      {/* The task is re-fetched rather than patched from the response: the
          completion touches the compliance instance and the billing state as
          well, and reconciling those by hand here would be a second copy of
          rules the server already applied. */}
      {completing && (
        <TaskCompletionFlow
          task={task}
          onClose={() => setCompleting(false)}
          onDone={() => fetchTask()}
        />
      )}
    </OrgAdminLayout>
  );
}

/* ------------------------------------------------------------------ */
/* Header row: name, status dropdown, Verify, timer                     */
/* ------------------------------------------------------------------ */
function TaskHeaderRow({ task, patchTask, onTimer, onComplete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [waitingPrompt, setWaitingPrompt] = useState(false);
  const [waitingDraft, setWaitingDraft] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const isDone = task.status === 'completed';

  const setStatus = (value) => {
    setMenuOpen(false);
    if (value === task.status) return;
    // Parking a task on the client is only useful if it records WHAT is being
    // waited for — "waiting on client" with no note tells the next person
    // nothing and turns the chase list into guesswork.
    if (value === 'waiting-client') {
      setWaitingDraft(task.waitingReason || '');
      setWaitingPrompt(true);
      return;
    }
    // ⚠️ COMPLETION IS NOT A FIELD EDIT and does not go through patchTask. It
    // files the compliance record, releases dependent tasks and drafts the
    // invoice, all of which have to succeed or roll back together — see
    // POST /api/tasks/:id/complete. Patching `status` here would close the task
    // and silently skip every one of those.
    if (value === 'completed') {
      onComplete();
      return;
    }
    patchTask({ status: value }, { status: value }).catch(() => {});
  };

  const confirmWaiting = () => {
    setWaitingPrompt(false);
    patchTask(
      { status: 'waiting-client', waitingReason: waitingDraft.trim() || null },
      { status: 'waiting-client', waitingReason: waitingDraft.trim() || null, waitingSince: new Date().toISOString() }
    ).catch(() => {});
  };

  // A completed task can only be reopened. The reopen sends any active status;
  // the server canonicalises it back to "Pending" (todo).
  const reopen = () => {
    setMenuOpen(false);
    patchTask({ status: 'todo' }, { status: 'todo', completedAt: null, inProgressAt: null }).catch(() => {});
  };

  return (
    <div className="td-header-row">
      <div className="td-header-left">
        {task.taskNumber != null && (
          <span className="td-task-number">TASK-{String(task.taskNumber).padStart(4, '0')}</span>
        )}
        <h1 className="td-task-name">{task.title}</h1>

        <div className="td-status-dd" ref={ref}>
          <button
            type="button"
            className={`td-status-pill td-status-${task.status}`}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {STATUS_LABEL[task.status] || task.status}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          {menuOpen && (
            <div className="td-status-menu" role="menu">
              {isDone ? (
                // Completed is a locked state: the only move out is a reopen.
                <button type="button" className="td-status-menu-item" onClick={reopen}>
                  <span className="td-status-dot td-status-todo" />
                  Reopen task
                </button>
              ) : (
                STATUS_OPTIONS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    className={`td-status-menu-item ${s.value === task.status ? 'is-active' : ''}`}
                    onClick={() => setStatus(s.value)}
                  >
                    <span className={`td-status-dot td-status-${s.value}`} />
                    {s.label}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="td-header-right">
        {isDone ? (
          <>
            <span className="td-verified"><CheckIcon /> Verified</span>
            <Button variant="secondary" size="sm" onClick={reopen}>Reopen</Button>
          </>
        ) : (
          <Button variant="secondary" size="sm" onClick={onComplete}>
            <CheckIcon /> Verify
          </Button>
        )}
        <button type="button" className="td-timer-btn" onClick={onTimer} title="Time log">
          <TimerIcon />
        </button>
      </div>

      <Modal
        isOpen={waitingPrompt}
        onClose={() => setWaitingPrompt(false)}
        title="What are we waiting on?"
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setWaitingPrompt(false)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={confirmWaiting}>Mark as waiting</Button>
          </>
        }
      >
        <p className="td-muted">
          This shows on the task list with the number of days it has been waiting, so whoever
          chases the client knows what to ask for.
        </p>
        <Input
          label="Waiting for"
          autoFocus
          placeholder="e.g. April–June bank statements"
          value={waitingDraft}
          onChange={(e) => setWaitingDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') confirmWaiting(); }}
        />
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Meta: Period, Description (inline), Client link                      */
/* ------------------------------------------------------------------ */
function MetaSection({ task, patchTask, locked }) {
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(task.description || '');

  const saveDesc = async () => {
    setEditingDesc(false);
    if (descDraft === (task.description || '')) return;
    patchTask({ description: descDraft }, { description: descDraft }).catch(() => {});
  };

  // A task can carry its client on `clientId` (single) OR in `clientIds[]`
  // (e.g. bulk-created / compliance tasks). Fall back so the Client field
  // isn't blank when only clientIds is set.
  const clientList = task.clientId ? [task.clientId] : (task.clientIds || []);
  const client = clientList[0] || null;
  const extraClients = clientList.length - 1;

  return (
    <div className={`td-meta${locked ? ' td-locked-region' : ''}`}>
      <div className="td-meta-item">
        <span className="td-meta-label">Period</span>
        <span className="td-meta-value">
          {task.period || '—'}
          {/* A generated occurrence says so, so nobody edits it expecting the
              change to carry to the rest of the series — it won't; the template
              is a different task. */}
          {task.recurrenceParentId && (
            <Link to={`/org-admin/tasks/${task.recurrenceParentId}`} className="td-meta-more td-meta-link">
              recurring
            </Link>
          )}
        </span>
      </div>

      {/* Only shown when the task was raised against a catalog service. */}
      {task.serviceId?.name && (
        <div className="td-meta-item">
          <span className="td-meta-label">Service</span>
          <span className="td-meta-value">{formatServiceName(task.serviceId.name)}</span>
        </div>
      )}

      <div className="td-meta-item td-meta-desc">
        <span className="td-meta-label">Description</span>
        {editingDesc ? (
          <span className="td-inline-edit">
            <input
              className="td-inline-input"
              autoFocus
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveDesc(); if (e.key === 'Escape') { setDescDraft(task.description || ''); setEditingDesc(false); } }}
              onBlur={saveDesc}
            />
          </span>
        ) : (
          <span className="td-meta-value td-editable" onClick={() => { setDescDraft(task.description || ''); setEditingDesc(true); }}>
            {task.description || 'Add a description'}
            <button className="td-pencil" aria-label="Edit description"><PencilIcon /></button>
          </span>
        )}
      </div>

      <div className="td-meta-item">
        <span className="td-meta-label">Client</span>
        {client?._id ? (
          <span className="td-meta-value">
            <Link to={`/org-admin/clients/${client._id}`} className="td-meta-link">
              {client.displayName}
            </Link>
            {extraClients > 0 && <span className="td-meta-more"> +{extraClients} more</span>}
          </span>
        ) : (
          <span className="td-meta-value">—</span>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Details tab                                                          */
/* ------------------------------------------------------------------ */
function DetailsTab({ task, orgUsers, createdByName, patchTask, setTask, toast, taskId, locked }) {
  return (
    <div className={`td-details-grid${locked ? ' td-locked-region' : ''}`}>
      <div className="td-col-left">
        <Card padding="md">
          <StatusStepper task={task} />

          <div className="td-field-grid">
            <InlineDateField label="Due Date" value={task.dueDate} onSave={(v) => patchTask({ dueDate: v || null }, { dueDate: v || null })} />
            <InlineDateField label="Target Date" value={task.targetDate} onSave={(v) => patchTask({ targetDate: v || null }, { targetDate: v || null })} />
            <div className="td-field">
              <span className="td-field-label">Created by</span>
              <span className="td-field-value">{createdByName}</span>
            </div>
            <div className="td-field">
              <span className="td-field-label">Retainer</span>
              <RetainerChip task={task} patchTask={patchTask} />
            </div>
          </div>

          <AssistantsSection task={task} orgUsers={orgUsers} patchTask={patchTask} />
          <TagsSection task={task} patchTask={patchTask} />
        </Card>
      </div>

      <div className="td-col-right">
        {/* Billing sits first: it was the last of four cards and people could not
            find it at all. What the client owes for this work is not a footnote. */}
        <BillingCard task={task} setTask={setTask} taskId={taskId} toast={toast} />
        <TaskChecklistCard task={task} setTask={setTask} taskId={taskId} />
        <TaskDependenciesCard task={task} setTask={setTask} taskId={taskId} locked={locked} />
        <TaskRecurrenceCard taskId={taskId} locked={locked} />
      </div>
    </div>
  );
}

const BILL_TYPES = [
  { value: 'professional_fees', label: 'Professional Fees' },
  { value: 'ope', label: 'OPE (reimbursable)' },
];

// Indian grouping (₹1,50,000), which is what the amount will be read back as.
const formatINR = (n) =>
  n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// A bill raised before this change has no createdAt (the migration backfilled it
// from the task, but a hand-inserted row might not), so guard rather than
// printing "Invalid Date".
const formatBillDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

// What each charge's invoice state means in one phrase. A charge sitting on a
// CANCELLED invoice is unbilled again (cancelling releases it — see
// financeController.cancelInvoice), so it says so rather than claiming credit
// for a document that no longer stands.
function chargeState(charge) {
  const inv = charge.invoice;
  if (!inv) return { cls: 'td-bill-state-open', label: 'Not billed yet' };
  if (inv.cancelled) {
    return { cls: 'td-bill-state-open', label: `Not billed — ${inv.number || 'invoice'} was cancelled` };
  }
  if (inv.status === 'draft') return { cls: 'td-bill-state-draft', label: 'On a draft invoice' };
  if (inv.status === 'proforma_sent') {
    return { cls: 'td-bill-state-draft', label: `Proforma ${inv.number || ''} sent`.trim() };
  }
  if (inv.status === 'paid') return { cls: 'td-bill-state-paid', label: `Paid — ${inv.number}` };
  if (inv.isOverdue) return { cls: 'td-bill-state-overdue', label: `Overdue — ${inv.number}` };
  return { cls: 'td-bill-state-billed', label: `Invoiced ${inv.number}` };
}

// A task carries a LIST of bills: the professional fee, plus any out-of-pocket
// expenses paid on the client's behalf (filing fee, stamp duty, courier), each
// with its own description. The task's billable flag and total are DERIVED from
// this list server-side, so there is nothing to keep in sync here.
//
// The card used to stop at the amounts, which left the one question people
// actually ask unanswered: has the client been billed for this? That state lives
// on the INVOICE LINES, never as a flag on the task (see Invoice.js), so it takes
// a fetch — /api/tasks/:id/billing resolves each charge to its invoice.
function BillingCard({ task, setTask, taskId, toast }) {
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [invoicing, setInvoicing] = useState(null);
  const navigate = useNavigate();
  const bills = task.billItems || [];
  const total = bills.reduce((sum, b) => sum + (b.amount || 0), 0);

  // Re-read whenever the bill lines change (adding one makes it unbilled work)
  // and after an invoice is raised from here.
  const billKey = bills.map((b) => `${b._id}:${b.amount}`).join(',');
  useEffect(() => {
    let cancelled = false;
    apiClient.get(`/api/tasks/${taskId}/billing`)
      .then((r) => !cancelled && setBilling(r.data))
      // A failure here must not blank the card — the amounts below come from the
      // task itself and are still worth showing.
      .catch(() => !cancelled && setBilling(null));
    return () => { cancelled = true; };
  }, [taskId, billKey]);

  const charges = billing?.charges || [];
  const unbilled = billing?.totals?.unbilled ?? null;
  const elsewhere = billing?.clientUnbilled;
  // What this task alone contributes, vs everything waiting for the client.
  const otherCount = elsewhere ? elsewhere.count - charges.filter((c) => !c.isBilled).length : 0;

  return (
    <Card padding="md" className="td-billing-card">
      {/* Action sits in the head, where the other cards keep theirs. */}
      <div className="td-card-head">
        <span className="td-card-title">Billing to client</span>
        {bills.length > 0 && (
          <button type="button" className="td-add-link" onClick={() => setOpen(true)}>
            Edit Billings
          </button>
        )}
      </div>

      {bills.length === 0 ? (
        <button type="button" className="td-add-link td-bill-empty-link" onClick={() => setOpen(true)}>
          + Add billing
        </button>
      ) : (
        <>
          <div className="td-bill-summary">
            <span className="td-bill-total-amount">{formatINR(total)}</span>
            <div className="td-bill-meta-line">
              <span>{bills.length} {bills.length === 1 ? 'charge' : 'charges'}</span>
              {unbilled != null && (
                <>
                  <span className="td-bill-dot">·</span>
                  <span className={unbilled > 0 ? 'td-bill-warn' : ''}>
                    {unbilled > 0 ? `${formatINR(unbilled)} not yet billed` : 'all billed'}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Charge -> invoice, one line each. This is the bit that was missing:
              the amounts were visible, their fate was not. */}
          {charges.length > 0 && (
            <ul className="td-bill-states">
              {charges.map((c) => {
                const st = chargeState(c);
                return (
                  <li key={c.billItemId} className="td-bill-state-row">
                    <span className={`td-bill-pill td-bill-pill-${c.billType}`}>
                      {c.billType === 'ope' ? 'OPE' : 'Fees'}
                    </span>
                    <span className="td-bill-state-desc">
                      {c.description || <em className="td-bill-nodesc">No description</em>}
                    </span>
                    <span className="td-bill-state-amt">{formatINR(c.amount)}</span>
                    <span className={`td-bill-state ${st.cls}`}>{st.label}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Raising the invoice needs the finance section — the same guard the
              API is behind, so the button is only offered to someone it will
              work for. */}
          {unbilled > 0 && billing?.clientId && (
            <PermissionGate requiredPermission="nav.finance">
              <div className="td-bill-actions">
                <Button
                  variant="primary" size="sm"
                  onClick={() => setInvoicing({
                    clientId: billing.clientId,
                    charges: charges.filter((c) => !c.isBilled).map((c) => ({
                      billItemId: c.billItemId, taskId: billing.taskId,
                      kind: c.billType, amount: c.amount,
                      description: c.description || task.title,
                    })),
                  })}
                >
                  Bill {formatINR(unbilled)} to {billing.clientName || 'client'}
                </Button>
                {/* Billing one task at a time is usually the wrong move — an
                    invoice normally gathers the month's work for that client. */}
                {otherCount > 0 && (
                  <button
                    type="button" className="td-add-link"
                    onClick={() => navigate(`/org-admin/finance?tab=unbilled&client=${billing.clientId}`)}
                  >
                    {otherCount} more {otherCount === 1 ? 'charge' : 'charges'} waiting for this
                    client ({formatINR(elsewhere.value)} in all) — bill together
                  </button>
                )}
              </div>
            </PermissionGate>
          )}
        </>
      )}

      {open && (
        <BillingModal
          task={task}
          setTask={setTask}
          taskId={taskId}
          toast={toast}
          onClose={() => setOpen(false)}
        />
      )}

      {invoicing && (
        <InvoiceBuilder
          preset={invoicing}
          onClose={() => setInvoicing(null)}
          onCreated={(id) => {
            setInvoicing(null);
            toast.success('Draft invoice created');
            navigate(`/org-admin/finance?tab=invoices&invoice=${id}`);
          }}
        />
      )}
    </Card>
  );
}

// The full billing editor: every line, plus add / edit / remove.
function BillingModal({ task, setTask, taskId, toast, onClose }) {
  const bills = task.billItems || [];
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ billType: 'professional_fees', amount: '', description: '' });
  const [saving, setSaving] = useState(false);

  const total = bills.reduce((sum, b) => sum + (b.amount || 0), 0);
  const byHead = (type) =>
    bills.filter((b) => b.billType === type).reduce((sum, b) => sum + (b.amount || 0), 0);
  // Subtotals only earn their space when both heads are actually present —
  // otherwise they just restate the total and print a pointless "OPE ₹0".
  const hasBothHeads =
    bills.some((b) => b.billType === 'professional_fees') && bills.some((b) => b.billType === 'ope');

  const openAdd = () => {
    setDraft({ billType: 'professional_fees', amount: '', description: '' });
    setEditingId(null);
    setAdding(true);
  };

  const openEdit = (bill) => {
    setDraft({
      billType: bill.billType,
      amount: bill.amount ?? '',
      description: bill.description || '',
    });
    setEditingId(bill._id);
    setAdding(false);
  };

  const cancel = () => { setAdding(false); setEditingId(null); };

  const save = async () => {
    if (String(draft.amount).trim() === '' || Number(draft.amount) < 0) {
      toast.danger('Enter an amount');
      return;
    }
    setSaving(true);
    try {
      const body = {
        billType: draft.billType,
        amount: Number(draft.amount),
        description: draft.description,
      };
      const res = editingId
        ? await apiClient.put(`/api/tasks/${taskId}/bills/${editingId}`, body)
        : await apiClient.post(`/api/tasks/${taskId}/bills`, body);
      setTask(res.data);
      cancel();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to save bill');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (bill) => {
    try {
      const res = await apiClient.delete(`/api/tasks/${taskId}/bills/${bill._id}`);
      setTask(res.data);
    } catch (err) {
      toast.danger('Failed to remove bill');
    }
  };

  const form = (
    <div className="td-billing-form">
      <Select
        label="Head"
        value={draft.billType}
        onChange={(e) => setDraft({ ...draft, billType: e.target.value })}
      >
        {BILL_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
      </Select>
      <Input
        label="Amount (₹)"
        type="number"
        min="0"
        step="0.01"
        placeholder="0.00"
        value={draft.amount}
        onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
      />
      <Input
        label="Description"
        placeholder="e.g. ROC filing fee"
        value={draft.description}
        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
      />
      <div className="td-billing-actions">
        <Button variant="secondary" size="sm" onClick={cancel}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={save} loading={saving}>
          {editingId ? 'Save' : 'Add bill'}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Billing — ${task.taskNumber != null ? `TASK-${String(task.taskNumber).padStart(4, '0')}` : 'Task'}`}
      footer={
        <>
          {!adding && !editingId && (
            <Button variant="secondary" onClick={openAdd}>+ Add bill</Button>
          )}
          <Button variant="primary" onClick={onClose}>Done</Button>
        </>
      }
    >
      {bills.length === 0 && !adding ? (
        <p className="td-billing-empty">No bills yet. Add a professional fee or an expense.</p>
      ) : (
        <div className="td-bill-list">
          {bills.map((bill) => (
            editingId === bill._id ? (
              <div key={bill._id}>{form}</div>
            ) : (
              <div key={bill._id} className="td-bill-row">
                <span className={`td-bill-pill td-bill-pill-${bill.billType}`}>
                  {bill.billType === 'ope' ? 'OPE' : 'Fees'}
                </span>

                <div className="td-bill-body">
                  <span className="td-bill-desc">
                    {bill.description || <em className="td-bill-nodesc">No description</em>}
                  </span>
                  <span className="td-bill-meta">
                    {/* When this bill was raised (stamped server-side on add). */}
                    {formatBillDate(bill.createdAt)}
                    <button type="button" className="td-bill-link" onClick={() => openEdit(bill)}>Edit</button>
                    <button type="button" className="td-bill-link td-bill-link-danger" onClick={() => remove(bill)}>Remove</button>
                  </span>
                </div>

                <span className="td-bill-amount">{formatINR(bill.amount)}</span>
              </div>
            )
          ))}

          {adding && form}

          {bills.length > 0 && (
            <div className="td-bill-total">
              {/* Fees and expenses settle differently — a client reimburses OPE
                  rather than paying it as a charge — so they're subtotalled apart. */}
              {hasBothHeads && (
                <div className="td-bill-subtotals">
                  <span>Fees {formatINR(byHead('professional_fees'))}</span>
                  <span>OPE {formatINR(byHead('ope'))}</span>
                </div>
              )}
              <div className="td-bill-total-row">
                <span>Total</span>
                <span className="td-billing-amount">{formatINR(total)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
function StatusStepper({ task }) {
  const current = STATUS_STEP[task.status] ?? 0;
  const steps = [
    { label: 'Pending', date: task.createdAt },
    { label: 'In-Progress', date: task.inProgressAt },
    { label: 'Completed', date: task.completedAt },
  ];
  return (
    <div className="td-stepper">
      {steps.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'future';
        return (
          <div key={s.label} className={`td-step td-step-${state}`}>
            <div className="td-step-top">
              <span className="td-step-dot">{i < current || (i === current && task.status === 'completed') ? <CheckIcon /> : i + 1}</span>
              {i < steps.length - 1 && <span className="td-step-line" />}
            </div>
            <div className="td-step-label">{s.label}</div>
            <div className="td-step-date">{fmtDate(s.date) || '—'}</div>
          </div>
        );
      })}
    </div>
  );
}

function InlineDateField({ label, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ? new Date(value).toISOString().split('T')[0] : '');

  const start = () => { setDraft(value ? new Date(value).toISOString().split('T')[0] : ''); setEditing(true); };
  const commit = () => { setEditing(false); onSave(draft).catch?.(() => {}); };

  return (
    <div className="td-field">
      <span className="td-field-label">{label}</span>
      {editing ? (
        <input
          type="date"
          className="td-inline-input"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        />
      ) : (
        <span className="td-field-value td-editable" onClick={start}>
          {fmtDate(value) || 'Not set'}
          <button className="td-pencil" aria-label={`Edit ${label}`}><PencilIcon /></button>
        </span>
      )}
    </div>
  );
}

function RetainerChip({ task, patchTask }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  if (task.retainer) {
    return (
      <span className="td-chip td-chip-link">
        {task.retainer}
        <button className="td-chip-x" onClick={() => patchTask({ retainer: null }, { retainer: null }).catch(() => {})} aria-label="Remove retainer"><CloseX /></button>
      </span>
    );
  }
  return adding ? (
    <input
      className="td-inline-input"
      autoFocus
      placeholder="Retainer name"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { setAdding(false); if (draft.trim()) patchTask({ retainer: draft.trim() }, { retainer: draft.trim() }).catch(() => {}); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setAdding(false); }}
    />
  ) : (
    <button className="td-add-link" onClick={() => { setDraft(''); setAdding(true); }}>+ Add retainer</button>
  );
}

// Assistants — the people helping the preparer.
//
// This card used to edit `Task.users[]`, a fourth people-list that meant "who's
// on it" and carried no consequences at all: being in it put no work on your
// list, let nobody log time for you, and budgeted no hours. `assistantIds` is
// what the app already means by "helping with this task", and it is wired into
// the time-log allow-list, the hour allocations, "my tasks" and handover.
// See src/utils/taskPeople.js.
function AssistantsSection({ task, orgUsers, patchTask }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const assistants = task.assistantIds || [];

  const openModal = () => {
    setSelected(assistants.map((u) => u._id));
    setOpen(true);
  };
  const toggle = (uid) => setSelected((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  const save = () => {
    setOpen(false);
    patchTask(
      { assistantIds: selected },
      { assistantIds: orgUsers.filter((u) => selected.includes(u._id)) }
    ).catch(() => {});
  };

  return (
    <div className="td-section">
      <div className="td-section-head">
        <span className="td-section-title">Assistants</span>
        <button className="td-pencil" onClick={openModal} aria-label="Edit assistants"><PencilIcon /></button>
      </div>
      {assistants.length === 0 ? (
        <div className="td-muted">No assistants assigned</div>
      ) : (
        <div className="td-avatars">
          {assistants.map((u) => (
            <span key={u._id} className="td-avatar-labelled">
              <Avatar name={u.name} size={28} />
              <span className="td-avatar-name">{u.name}</span>
            </span>
          ))}
        </div>
      )}

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Assign assistants"
        footer={<><Button variant="secondary" size="sm" onClick={() => setOpen(false)}>Cancel</Button><Button variant="primary" size="sm" onClick={save}>Save</Button></>}
      >
        <div className="td-user-picker">
          {orgUsers.map((u) => (
            <label key={u._id} className="td-user-option">
              <input type="checkbox" checked={selected.includes(u._id)} onChange={() => toggle(u._id)} />
              <Avatar name={u.name} size={26} />
              <span>{u.name}</span>
            </label>
          ))}
        </div>
      </Modal>
    </div>
  );
}

function TagsSection({ task, patchTask }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const tags = task.tags || [];

  const addTag = () => {
    const t = draft.trim();
    if (!t || tags.includes(t)) { setDraft(''); return; }
    const next = [...tags, t];
    setDraft('');
    patchTask({ tags: next }, { tags: next }).catch(() => {});
  };
  const removeTag = (t) => {
    const next = tags.filter((x) => x !== t);
    patchTask({ tags: next }, { tags: next }).catch(() => {});
  };

  return (
    <div className="td-section">
      <div className="td-section-head">
        <span className="td-section-title">Tags</span>
        <button className="td-pencil" onClick={() => setEditing((e) => !e)} aria-label="Edit tags"><PencilIcon /></button>
      </div>
      {tags.length === 0 && !editing ? (
        <div className="td-muted">There are no Tags</div>
      ) : (
        <div className="td-tags">
          {tags.map((t) => (
            <span key={t} className="td-chip">
              {t}
              {editing && <button className="td-chip-x" onClick={() => removeTag(t)} aria-label={`Remove ${t}`}><CloseX /></button>}
            </span>
          ))}
          {editing && (
            <input
              className="td-tag-input"
              autoFocus
              placeholder="Add tag + Enter"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addTag(); }}
            />
          )}
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Legacy inline attachments                                            */
/* ------------------------------------------------------------------ */
// `task.attachments[]` predates the document store: bare {fileName, fileUrl}
// strings with no storage behind them. Nothing writes to it any more, but old
// tasks still carry rows, so they stay visible (read-only) rather than
// disappearing when the tab was rebuilt.
function LegacyAttachments({ task }) {
  const files = task.attachments || [];
  if (files.length === 0) return null;
  return (
    <Card padding="md" className="td-legacy-attachments">
      <div className="td-card-head">
        <span className="td-card-title">Older attachments</span>
        <span className="td-muted">Links added before the document store</span>
      </div>
      <div className="td-doc-list">
        {files.map((f, i) => (
          <a key={i} className="td-doc-row" href={f.fileUrl} target="_blank" rel="noreferrer">
            <span>🔗 {f.fileName}</span>
            <span className="td-muted">{fmtDate(f.uploadedAt)}</span>
          </a>
        ))}
      </div>
    </Card>
  );
}

export default OrgAdminTaskDashboard;
